import React, {useState, useEffect} from 'react';
import {useStoreState} from 'easy-peasy';
import {DateTime} from 'luxon';
import Spinner from '@/components/ui/Spinner';
import useData from '@/utils/useData';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {buildURL} from '@/utils/urlBuilder';
import {Agent} from '@/entities/agent';
import i18n from 'i18n';
import axios from 'axios';
import Icon from '@/components/icons/Icon';
import Tooltip from '@/components/tooltips/Tooltip';
import AgentTeamAssignmentEditModal from './AgentTeamAssignmentEditModal';
import type {TeamAssignment} from './AgentTeamAssignmentEditModal';
import AgentTeamAssignmentAddModal from './AgentTeamAssignmentAddModal';
import TableComponent from '@/components/table/Table';
import {useAlert} from '@/components/alerts';
import HelpTextBlock from 'components/help_text/HelpTextBlock';
import {useFormattedDateTime, useFormattedDate} from '@/components/utils/FormattedDateTime';
import {extractErrorMessage} from '@/utils/errorHandling';
import {RestrictedFieldIndicator} from '@/components/utils/DataNotAvailable';

interface AgentTeamAssignments {
    agentId: number;
    agentName: string;
    agentExternalId: string;
    teamAssignments: TeamAssignment[];
}

interface AgentData extends Agent {
    teamId: string;
    teamName: string;
}

interface AgentTeamViewProps {
    agentId?: string;
}

const AgentTeamView: React.FC<AgentTeamViewProps> = React.memo(({agentId}) => {
    const activeStoryObject = useStoreState((state: any) => state?.activeStoryObject?.activeStoryObject);
    const agentIdToUse = agentId || activeStoryObject;
    const {showError} = useAlert();
    const {userData} = useOptimizedConfig();
    const {formatDate} = useFormattedDate();
    // Don't make API calls if we don't have an agent ID
    if (!agentIdToUse) {
        return (
            <div className="alert alert-warning" role="alert">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                {i18n.t('jt_no_agent_selected.text', 'No agent selected')}
            </div>
        );
    }

    // Check if URLs are available and build them safely
    let agentDataUrl = '';
    let teamAssignmentsUrl = '';

    if (window.URLS?.agentDetails && window.URLS?.agentTeamAssignments) {
        // Use window.URLS if available (preferred method)
        agentDataUrl = window.URLS.agentDetails.replace(':agent_id', agentIdToUse);
        teamAssignmentsUrl = window.URLS.agentTeamAssignments.replace(':agent_id', agentIdToUse);
    } else {
        // Fallback to direct URL construction if window.URLS not ready
        agentDataUrl = `/internal_api/v1/agents/${agentIdToUse}`;
        teamAssignmentsUrl = `/internal_api/v1/agents/${agentIdToUse}/team_assignments`;
    }

    // If still no URLs, show loading
    if (!agentDataUrl || !teamAssignmentsUrl) {
        return (
            <Spinner
                variant="border"
                centered={true}
                minHeight="200px"
            />
        );
    }

    const [agentData, setAgentData] = useState<AgentData | null>(null);
    const [teamAssignments, setTeamAssignments] = useState<AgentTeamAssignments | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState<TeamAssignment | null>(null);
    const [saving, setSaving] = useState(false);

    // Add new assignment modal state
    const [showAddModal, setShowAddModal] = useState(false);

    // Show error toast when there's an error
    useEffect(() => {
        if (error) {
            showError(i18n.t('jt_error_loading_data.text', 'Error loading agent team data. Please try refreshing the page.'));
        }
    }, [error, showError]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const [agentResponse, teamResponse] = await Promise.all([
                    axios.get<AgentData>(agentDataUrl),
                    axios.get<AgentTeamAssignments>(teamAssignmentsUrl)
                ]);

                setAgentData(agentResponse.data);
                setTeamAssignments(teamResponse.data);
            } catch (err) {
                console.error('Error fetching agent team data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        if (agentDataUrl && teamAssignmentsUrl) {
            fetchData();
        }
    }, [agentDataUrl, teamAssignmentsUrl]);

    const handleEditAssignment = (assignment: TeamAssignment) => {
        setEditingAssignment(assignment);
        setShowEditModal(true);
    };

    const handleSaveAssignment = async (updatedAssignment: TeamAssignment) => {
        if (!updatedAssignment || !agentIdToUse) {
            console.error('Missing assignment data or agent ID');
            return;
        }

        try {
            setSaving(true);

            // Use the simpler URL pattern that matches the backend route
            const updateUrl = `/internal_api/v1/agents/assign_team/${updatedAssignment.id}`;

            // Format the active_between field as expected by the API
            // The API expects format: "start_date,end_date" or "start_date," for ongoing
            const startDate = updatedAssignment.startDate || '';
            const endDate = updatedAssignment.endDate || '';
            const activeBetween = `${startDate},${endDate}`;

            const payload = {
                team_id: updatedAssignment.teamId,
                agent_id: parseInt(agentIdToUse),
                active_between: activeBetween
            };
            const response = await axios.put(updateUrl, payload);

            // Refresh the data
            const teamResponse = await axios.get<AgentTeamAssignments>(teamAssignmentsUrl);
            setTeamAssignments(teamResponse.data);

            setShowEditModal(false);
            setEditingAssignment(null);
        } catch (err) {
            console.error('Error updating team assignment:', err);
            const errorMessage = extractErrorMessage(err, 'Failed to update assignment');
            showError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setEditingAssignment(null);
    };

    const handleAddNewAssignment = () => {
        setShowAddModal(true);
    };

    const handleSaveNewAssignment = async (newAssignmentData: any) => {
        if (!agentIdToUse) {
            console.error('Missing agent ID');
            return;
        }

        try {
            setSaving(true);

            const createUrl = window.URLS?.assignAgentToTeamResource || '/internal_api/v1/agents/assign_team';

            const payload = {
                team_id: parseInt(newAssignmentData.teamId),
                agent_id: parseInt(agentIdToUse),
                active_between: `${newAssignmentData.startDate},${newAssignmentData.endDate || ''}`
            };
            await axios.post(createUrl, payload);

            // Refresh the data
            const teamResponse = await axios.get<AgentTeamAssignments>(teamAssignmentsUrl);
            setTeamAssignments(teamResponse.data);

            setShowAddModal(false);
        } catch (err) {
            console.error('Error creating team assignment:', err);
            const errorMessage = extractErrorMessage(err, 'Failed to create assignment');
            showError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleCloseAddModal = () => {
        setShowAddModal(false);
    };

    if (loading) {
        return (
            <Spinner
                variant="border"
                centered={true}
                minHeight="200px"
            />
        );
    }

    // Handle API errors
    // Error is handled via toast notification in useEffect above

    // Handle case where data loaded but no assignments exist
    if (!teamAssignments?.teamAssignments?.length) {
        return (
            <div>
                {/* Add New Assignment Button */}
                <div className="d-flex justify-content-end mb-2">
                    <button
                        className="btn btn-primary"
                        onClick={handleAddNewAssignment}
                        type="button"
                    >
                        <Icon name="mdi-plus" className="mr-2" />
                        {i18n.t('jt_add_team_assignment.text', 'Add Team Assignment')}
                    </button>
                </div>

                <div className="alert alert-info" role="alert">
                    <i className="fas fa-info-circle mr-2"></i>
                    {i18n.t('jt_no_team_assigned_to_this_agent.text', 'No team assigned to this agent')}
                </div>

                {/* Add Team Assignment Modal */}
                <AgentTeamAssignmentAddModal
                    show={showAddModal}
                    onSave={handleSaveNewAssignment}
                    onClose={handleCloseAddModal}
                    saving={saving}
                />
            </div>
        );
    }

    const assignments = teamAssignments?.teamAssignments || [];
    // Sort all assignments with active ones first, then by start date (newest first)
    const sortedAssignments = assignments
        .filter(assignment => assignment)
        .sort((a, b) => {
            // Active assignments come first
            if (a.isActive && !b.isActive) {return -1;}
            if (!a.isActive && b.isActive) {return 1;}

            // Then sort by start date (newest first)
            if (!a?.startDate || !b?.startDate) {return 0;}
            try {
                return DateTime.fromISO(b.startDate).toMillis() - DateTime.fromISO(a.startDate).toMillis();
            } catch {
                return 0;
            }
        });

    // Transform assignments for TableComponent
    const tableData = sortedAssignments.map((assignment) => {
        const startDate = assignment.startDate ? DateTime.fromISO(assignment.startDate) : null;
        const endDate = assignment.endDate ? DateTime.fromISO(assignment.endDate) : null;
        let duration = i18n.t('th_not_available.text', '-');

        if (startDate) {
            const endDateToUse = assignment.isActive && !endDate ? DateTime.now() : endDate;

            if (endDateToUse) {
                const diffTime = endDateToUse.toMillis() - startDate.toMillis();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays < 30) {
                    duration = diffDays === 1 ? '1 day' : `${diffDays} days`;
                } else if (diffDays < 365) {
                    const months = Math.floor(diffDays / 30);
                    duration = months === 1 ? '1 month' : `${months} months`;
                } else {
                    const years = Math.floor(diffDays / 365);
                    const remainingMonths = Math.floor((diffDays % 365) / 30);
                    if (remainingMonths > 0) {
                        const yearText = years === 1 ? '1 year' : `${years} years`;
                        const monthText = remainingMonths === 1 ? '1 month' : `${remainingMonths} months`;
                        duration = `${yearText}, ${monthText}`;
                    } else {
                        duration = years === 1 ? '1 year' : `${years} years`;
                    }
                }
            }
        }

        return {
            id: assignment.id,
            teamName: assignment.teamName,
            teamShortname: assignment.teamShortname,
            startDate: assignment.startDate,
            endDate: assignment.endDate,
            duration,
            isActive: assignment.isActive,
            assignment // Keep original assignment for actions
        };
    });

    // Define columns for TableComponent
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const teamRestricted = restrictedFieldKeys.includes('team_pii');

    const columns = [
        {
            header: i18n.t('th_team_name.text', 'Team Name'),
            accessorKey: 'teamName',
            cell: ({row}: any) => {
                if (teamRestricted) {
                    return <RestrictedFieldIndicator />;
                }
                return (
                    <div>
                        <strong>{row.original.teamName || '-'}</strong>
                        {row.original.teamShortname && (
                            <small className="text-muted d-block">({row.original.teamShortname})</small>
                        )}
                    </div>
                );
            },
        },
        {
            header: i18n.t('th_start_date.text', 'Start Date'),
            accessorKey: 'startDate',
            cell: ({row}: any) =>
                row.original.startDate
                    ? formatDate(row.original.startDate, {setZone: false})
                    : i18n.t('th_not_available.text', '-'),
        },
        {
            header: i18n.t('th_end_date.text', 'End Date'),
            accessorKey: 'endDate',
            cell: ({row}: any) =>
                row.original.isActive && !row.original.endDate
                    ? i18n.t('th_ongoing.text', 'Ongoing')
                    : row.original.endDate
                        ? formatDate(row.original.endDate, {setZone: false})
                        : i18n.t('th_not_available.text', '-'),
        },
        {
            header: i18n.t('th_duration.text', 'Duration'),
            accessorKey: 'duration',
        },
        {
            header: i18n.t('th_status.text', 'Status'),
            accessorKey: 'isActive',
            cell: ({row}: any) =>
                row.original.isActive ? (
                    <span className="badge bg-success text-white py-1">
                        {i18n.t('th_active.text', 'Active')}
                    </span>
                ) : (
                    <span className="badge bg-secondary text-white py-1">
                        {i18n.t('th_past.text', 'Past')}
                    </span>
                ),
        },
        {
            header: i18n.t('th_action.text', 'Action'),
            accessorKey: 'id',
            cell: ({row}: any) => (
                <Tooltip content={i18n.t('jt_edit_team_assignment.text', 'Edit Team Assignment')} placement="top">
                    <button
                        className="btn btn-outline-info btn-sm"
                        onClick={() => handleEditAssignment(row.original.assignment)}
                        type="button"
                    >
                        <Icon name="mdi-settings" />
                    </button>
                </Tooltip>
            ),
        },
    ];

    return (
        <div className="space-y-4">
            <HelpTextBlock helpText={i18n.t('ht_agent_team.text')} />
            {/* Add New Assignment Button */}
            <div className="d-flex justify-content-end mb-2">
                <button
                    className="btn btn-primary"
                    onClick={handleAddNewAssignment}
                    type="button"
                >
                    <Icon name="mdi-plus" className="mr-2" />
                    {i18n.t('jt_add_team_assignment.text', 'Add Team Assignment')}
                </button>
            </div>

            {/* Team Assignments Table */}
            {sortedAssignments.length > 0 && (
                <div className="card">
                    <div className="card-body">
                        <TableComponent data={tableData} columns={columns} />
                    </div>
                </div>
            )}

            {/* No assignments message */}
            {sortedAssignments.length === 0 && (
                <div className="alert alert-info" role="alert">
                    <i className="fas fa-info-circle mr-2"></i>
                    {i18n.t('jt_no_team_assignments_found.text')}
                </div>
            )}


            {/* Add Team Assignment Modal */}
            <AgentTeamAssignmentAddModal
                show={showAddModal}
                onSave={handleSaveNewAssignment}
                onClose={handleCloseAddModal}
                saving={saving}
            />

            {/* Edit Team Assignment Modal */}
            <AgentTeamAssignmentEditModal
                show={showEditModal}
                assignment={editingAssignment}
                onSave={handleSaveAssignment}
                onClose={handleCloseModal}
                saving={saving}
            />
        </div>
    );
});

export default AgentTeamView;
